/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-globals */
import momentz from "moment-timezone";
import { isLATUser, LoanStage } from "@toorak/tc-common-fe-sdk";
import { currencyToNumber, formatValueByType, isEmptyValue, sanitizeCurrency, sanitizePercentage } from "../../utils/formatChecks";
import { getLoanType } from "../../config/config";
import { ToorakProductEnum } from "../../create-loan/constants/loanCreationDropDownValues";


export function isFalsyButNotZero(value: number): Boolean {
  return !value && value !== 0;
}
export interface KeyMetricFieldType {
  fieldId: string;
  fieldLabel: string;
  id?: string;
  fieldType: string;
  getValue: any;
  popOverData?: any;
  showInStage?: LoanStage[];
  redirectionType?: string;
  customValue?: any;
  isHidden?: any;
  width?: number | string;
  height?: string;
}
export interface KeyMetricSectionType {
  sectionId: string;
  sectionLabel: string;
  fields: any;
  isHidden?: any;
  type?: string
}

export const getAccumlatedPropertyValue = (data: any, key: string) => {
  const { propertyDetails } = data;
  let valueToReturn: any = null;
  if (propertyDetails) {
    propertyDetails.forEach((element: any) => {
      const { propertyEconomics } = element;
      if (!isEmptyValue(propertyEconomics?.[key])) {
        const cleanedValue = currencyToNumber(propertyEconomics?.[key]);
        if (valueToReturn) {
          valueToReturn += Number(cleanedValue);
        } else {
          valueToReturn = Number(cleanedValue);
        }
      }
    });
  }
  return `${valueToReturn}`;
};

export const getAccumlatedRuleResultPropertyValue = (
  propertiesResults: any,
  objKey: string,
  key: string
) => {
  let valueToReturn: any = null;
  if (propertiesResults) {
    Object.keys(propertiesResults ?? {}).forEach((name: any) => {
      const resultElement = propertiesResults[name];
      const value = resultElement?.[objKey]?.[key];
      if (!isEmptyValue(value)) {
        const cleanedValue = currencyToNumber(value);
        if (valueToReturn) {
          valueToReturn += Number(cleanedValue);
        } else {
          valueToReturn = Number(cleanedValue);
        }
      }
    });
  }
  return valueToReturn ? `${valueToReturn}` : "";
};

export const getKeyDealMetricsFieldMap = (
  data?: any
): Array<KeyMetricSectionType> => {
  const loanType = data?.loanType;
  const loanStage = data?.loanStage;
  switch (loanType) {
    case getLoanType[0].displayValue:
      return loanStage === "FES" ? fesBridgeLoanKeyMetricSection(data) : prePostBridgeLoanKeyMetricSection(data);
    case getLoanType[1].displayValue:
      return investorDSCRLoanKeyMetricSection;
    default:
      return [];
  }
};
/** --------------------------- BridgeLoan section start ---------------------------*/
export const bridgeLoanGeneralFields: KeyMetricFieldType[] = [
  {
    fieldId: "loanId",
    fieldLabel: "Loan ID",
    fieldType: "string",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanInfo?.primaryLoanId;
    }
  },
  {
    fieldId: "originator",
    fieldLabel: "Originator",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.originatorInfo?.accountName;
    }
  },
  {
    fieldId: "toorakProduct",
    fieldLabel: "Toorak Product",
    fieldType: "string",
    getValue: (data: any) =>
      data?.loanDetails?.loanInfo?.toorakProduct ?? ToorakProductEnum.Rehab
  },
  {
    fieldId: "address",
    fieldLabel: "Full Address",
    fieldType: "string",
    redirectionType: "googleMap",
    getValue: (data: any) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty > 1) {
          return `Multiple Properties-${numberOfProperty}`;
        }
        const { propertyLocation } = propertyDetails[0];
        const { addressLine1, city, state, postalCode } = propertyLocation;
        return `${addressLine1}, ${city}, ${state}, ${postalCode}`;
      }
    },
    popOverData: (data: any) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty <= 1) {
          return [];
        }
        const propertyAddressArray = propertyDetails.map((ele: any) => {
          const { propertyLocation } = ele;
          const { addressLine1, city, state, postalCode } = propertyLocation;
          return `${addressLine1}, ${city}, ${state}, ${postalCode}`;
        });
        return propertyAddressArray;
      }
      return [];
    }
  },
  {
    fieldId: "toorakCharacterization",
    fieldLabel: "Toorak Characterization",
    fieldType: "string",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanResult?.[loanId]?.loanCharacterisations?.loanCharacterisation ||
        loanResult?.[loanId]?.loanCharacterisation?.loanCharacterisation
      );
    }
  }
];
export const bridgeLoanBorrowerInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "experience",
    fieldLabel: "Experience",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult?.[loanId]?.borrowerInfo?.experience;
    },
  },
  {
    fieldId: "creditScore",
    fieldLabel: "Credit Score",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult?.[loanId]?.borrowerInfo?.originalCreditScoreMedian;
    },
  },
  {
    fieldId: "isForeignNational",
    fieldLabel: "Foreign National",
    fieldType: "string",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      const fn = loanResult?.[loanId]?.borrowerInfo?.isForeignNational;
      if (isEmptyValue(fn)) return null;
      const value = fn === "N" ? "No" : "Yes";
      return value;
    },
  }
];

export const bridgeLoanLoanInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "loanStructure",
    fieldLabel: "Loan Structure",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanInfo?.loanStructure;
    }
  },
  {
    fieldId: "loanPurpose",
    fieldLabel: "Loan Purpose",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails, ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanDetails?.loanInfo?.loanPurpose ??
        loanResult?.[loanId]?.loanCharacterisation?.loanPurpose ??
        loanResult?.[loanId]?.loanCharacterisations?.loanPurpose
      );
    }
  },
  {
    fieldId: "codLoan",
    fieldLabel: "COD Loan",
    fieldType: "currency",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.cutOffDateLoanAmount;
    }
  },
  {
    fieldId: "codMaxLoan",
    fieldLabel: "COD Max Loan",
    fieldType: "currency",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.cutOffDateMaximumLoanAmount;
    }
  },
  {
    fieldId: "requestedInitialLoan",
    fieldLabel: "Requested Initial Loan",
    fieldType: "currency",
    showInStage: [LoanStage.fes],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.originalLoanAmount;
    }
  },
  {
    fieldId: "requestedMaxLoan",
    fieldLabel: "Requested Max Loan",
    fieldType: "currency",
    showInStage: [LoanStage.fes],
    getValue: (data: any) => {
      const { loanDetails, loanStage } = data;
      if (loanStage === LoanStage.fes) {
        const budgetAmount =
          loanDetails?.loanSizerType !== ToorakProductEnum.GroundUp
            ? loanDetails?.loanEconomics?.totalBudgetAmount
            : loanDetails?.loanEconomics?.rehabAmount;
        const valueToReturn =
          Number(
            currencyToNumber(loanDetails?.loanEconomics?.originalLoanAmount)
          ) + Number(currencyToNumber(budgetAmount));
        return valueToReturn.toString();
      }
      return loanDetails?.loanEconomics?.cutOffDateMaximumLoanAmount;
    }
  },
  {
    fieldId: "interestReserve",
    fieldLabel: "Interest Reserve",
    fieldType: "currency",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.financedInterestReserve;
    }
  },
  {
    fieldId: "interestRate",
    fieldLabel: "Interest Rate",
    showInStage: [LoanStage.pre, LoanStage.post],
    fieldType: "percentageWithout100x",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.interestRateAsOfCutOffDate;
    }
  },
  {
    fieldId: "cashOut",
    fieldLabel: "Cash Out",
    fieldType: "string",
    showInStage: [LoanStage.fes],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanInfo?.cashoutFlag;
    }
  },
  {
    fieldId: "cashOutAmount",
    fieldLabel: "Cash Out",
    fieldType: "currency",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.cashOutAmount;
    }
  },
  {
    fieldId: "DSCRCalculation",
    fieldLabel: "DSCR Calculation",
    fieldType: "string",
    // showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanEconomics?.dscrCalculation;
    }
  },
  {
    fieldId: "DSCR",
    fieldLabel: "DSCR",
    fieldType: "string",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      // if (loanStage === LoanStage.fes) {
      //   return loanResult[loanId].loanEconomics?.dscrCalculation;
      // }
      return loanResult[loanId].loanEconomics?.dscrRequirementTest;
    }
  },
  {
    fieldId: "rentalYield",
    fieldLabel: "Rental Yield",
    fieldType: "percentage",
    showInStage: [LoanStage.fes],
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].guidelineOutput.rentYieldCalculation;
      }
      return loanResult[loanId].loanEconomics.rentYieldCalculation;
    }
  },
  {
    fieldId: "debtYield",
    fieldLabel: "Debt Yield",
    fieldType: "percentage",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].multiFamilyMixedUse.actualDebtYield;
    }
  },
  {
    fieldId: "term",
    fieldLabel: "Term",
    fieldType: "string",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanFeatures.originalTermToMaturity;
    }
  },
  {
    fieldId: "profitability",
    fieldLabel: "Profitability",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].guidelineOutput.profitabilityCalculation;
      }
      return loanResult[loanId].loanEconomics.profitabilityCalculation;
    }
  },
  {
    fieldId: "totalBudgetAmount",
    fieldLabel: "Total Rehab Budget",
    fieldType: "currency",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.totalBudgetAmount;
    }
  },
  {
    fieldId: "initialInterestPayment",
    fieldLabel: "Interest Payment On Day 1 Loan Amount",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanEconomics?.initialInterestPayment;
    }
  },
  {
    fieldId: "maxInterestPayment",
    fieldLabel: "Max Interest Payment",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanEconomics?.maxInterestPayment;
    }
  },
  {
    fieldId: "loanStructureType",
    fieldLabel: "Loan Structure",
    fieldType: "string",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanResult[loanId]?.loanCharacterisation?.loanStructureType ??
        loanResult[loanId]?.loanCharacterisations?.loanStructureType
      );
    }
  },
  {
    fieldId: "downPaymentReserve",
    fieldLabel: "Down Payment Reserve",
    fieldType: "currency",
    getValue: (data: any) => {
      const { loanDetails } = data;
      const { loanEconomics } = loanDetails;
      if (!loanEconomics) return "";
      return loanEconomics.downPaymentReserve;
    }
  },
  {
    fieldId: "financedBudget",
    fieldLabel: "Financed Budget Amount",
    fieldType: "currency",
    getValue: (data: any) => {
      const { loanStage, loanDetails, ruleResult } = data;
      const firstKey = Object.keys(ruleResult?.loanResult)[0];
      const value =
        ruleResult?.loanResult?.[firstKey]?.loanEconomics
          ?.financedBudgetAmountCalculated ??
        loanDetails?.loanEconomics?.financedBudgetAmount;
      if (loanStage === LoanStage.fes) {
        return loanDetails?.loanEconomics?.rehabAmount;
      }
      return value;
    }
  }
];
export const bridgeLoanPropertyInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "propertyType",
    fieldLabel: "Property Type",
    fieldType: "string",
    getValue: (data: any, idx?: number, isAccumulated?: boolean) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty > 1 && isAccumulated) {
          return `Multiple Properties-${numberOfProperty}`;
        }
        const { propertyinfo } = propertyDetails[idx ?? 0];
        const propertyType =
          propertyinfo.propertyType === "SFR"
            ? "Single Family Detached"
            : propertyinfo.propertyType;
        return propertyType;
      }
    },
    popOverData: (data: any, idx?: number, isAccumulated?: boolean) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty <= 1) {
          return [];
        }
        if (isAccumulated) {
          const propertyAddressArray = propertyDetails.map((ele: any) => {
            const { propertyLocation } = ele;
            const { addressLine1, city, state, postalCode } = propertyLocation;
            return `${addressLine1}, ${city}, ${state}, ${postalCode}`;
          }) ?? [];
          return propertyAddressArray;
        } else {
          const { propertyLocation } = propertyDetails[idx ?? 0];
          const { addressLine1, city, state, postalCode } = propertyLocation;
          return [`${addressLine1}, ${city}, ${state}, ${postalCode}`];
        }
      }
      return [];
    }
  },
  {
    fieldId: "units",
    fieldLabel: "Units",
    fieldType: "number",
    getValue: (data: any, idx?: number, isAccumulated?: boolean) => {
      const { propertyDetails } = data;
      let numberOfUnits: any = null;
      if (propertyDetails) {
        if (isAccumulated) {
          propertyDetails.forEach((element: any) => {
            const { propertyinfo } = element;
            if (!isEmptyValue(propertyinfo.propertyUnits)) {
              if (numberOfUnits) {
                numberOfUnits += Number(propertyinfo.propertyUnits);
              } else {
                numberOfUnits = Number(propertyinfo.propertyUnits);
              }
            }
          });
        } else {
          numberOfUnits =
            propertyDetails?.[idx ?? 0]?.propertyinfo?.propertyUnits;
        }
      }
      return numberOfUnits;
    }
  },
  {
    fieldId: "asIs",
    fieldLabel: "As-Is",
    fieldType: "currency",
    getValue: (data: any, idx?: number, isAccumulated?: boolean, property?: any) => {
      const propertiesResults = data?.ruleResult?.propertiesResults ?? {};
      if (isAccumulated) {
        if (data?.loanStage === "FES")
          return getAccumlatedPropertyValue(data, "originalAsIsValueForCalculation");
        else
          return getAccumlatedRuleResultPropertyValue(
            propertiesResults,
            "loanFeatures",
            "originalAsIsValueForCalculation"
          );
      }
      if (data?.loanStage === "FES")
        // return data?.propertyDetails?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.propertyEconomics?.originalAsIsValueForCalculation;
        return property?.propertyEconomics?.originalAsIsAppraisalValue;
      else
        return propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.originalAsIsValueForCalculation ?? "";
    }
  },
  {
    fieldId: "Final As-Is",
    fieldLabel: "Final As-Is",
    fieldType: "currency",
    isHidden: (data: any) => !isLATUser(),
    getValue: (data: any, idx?: number, isAccumulated?: boolean, property?: any) => {
      const propertiesResults = data?.ruleResult?.propertiesResults ?? {};
      if (isAccumulated) {
        if (data?.loanStage === "FES")
          return getAccumlatedPropertyValue(data, "asIsValueForCalculation");
        else
          return getAccumlatedRuleResultPropertyValue(
            propertiesResults,
            "loanFeatures",
            "asIsValueForCalculation"
          );
      }
      if (data?.loanStage === "FES")
        return data?.propertyDetails?.[property?.loanPropertyId ??  (idx ?? 0) + 1]?.propertyEconomics?.asIsValueForCalculation;
      else
        return propertiesResults?.[property?.loanPropertyId ??  (idx ?? 0) + 1]?.loanFeatures?.asIsValueForCalculation ?? "";
    }
  },
  {
    fieldId: "arv",
    fieldLabel: "ARV",
    fieldType: "currency",
    getValue: (data: any, idx?: number, isAccumulated?: boolean, property?: any) => {
      const propertiesResults = data?.ruleResult?.propertiesResults ?? {};
      if (isAccumulated) {
        if (data?.loanStage === "FES")
          return getAccumlatedPropertyValue(
            data,
            "originalAsRepairedAppraisedValue"
          );
        else
          return getAccumlatedRuleResultPropertyValue(
            propertiesResults,
            "loanFeatures",
            "arvValueForCalculation"
          );
      }
      if (data?.loanStage === "FES")
        if(data?.loanType === "BridgeLoan")
          return property?.propertyEconomics?.originalAsRepairedAppraisedValue;
        else
          return data?.propertyDetails?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.propertyEconomics?.originalArvValueForCalculation;
      else
        return propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.originalArvValueForCalculation ?? "";
    }
  },
  {
    fieldId: "Final Arv",
    fieldLabel: "Final ARV",
    fieldType: "currency",
    isHidden: (data: any) => !isLATUser(),
    getValue: (data: any, idx?: number, isAccumulated?: boolean, property?: any) => {
      const propertiesResults = data?.ruleResult?.propertiesResults ?? {};
      if (isAccumulated) {
        if (data?.loanStage === "FES")
          return getAccumlatedPropertyValue(data, "arvValueForCalculation");
        else
          return getAccumlatedRuleResultPropertyValue(
            propertiesResults,
            "loanFeatures",
            "arvValueForCalculation"
          );
      }
      if (data?.loanStage === "FES")
        return data?.propertyDetails?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.propertyEconomics?.arvValueForCalculation;
      else
        return propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.arvValueForCalculation ?? "";
    }
  },
  {
    fieldId: "ARV/As-Isperunit",
    fieldLabel: "ARV/As-Is per unit",
    fieldType: "string",
    getValue: (data: any, idx?: number, isAccumulated?: boolean, property?: any) => {
      const { ruleResult, loanStage } = data;
      const { propertiesResults } = ruleResult;
      if (!propertiesResults) return "";
      const propertyIds = Object.keys(propertiesResults);
      if (propertyIds.length > 1 && isAccumulated) {
        return `Multiple Properties-${propertyIds.length}`;
      }
      if (loanStage === LoanStage.fes) {
        const { minimumArvPerUnit, minimumAsIsPerUnit } =
          propertiesResults[propertyIds[0]]?.guidelineOutput;
        let returnValue = "";
        if (!isEmptyValue(minimumArvPerUnit)) {
          returnValue = `ARV: ${formatValueByType(
            minimumArvPerUnit,
            "currency"
          )}`;
        } else {
          returnValue = "ARV: N/A";
        }
        if (!isEmptyValue(minimumAsIsPerUnit)) {
          returnValue += `; As-Is: ${formatValueByType(
            minimumAsIsPerUnit,
            "currency"
          )}`;
        } else {
          returnValue += "; As-Is: N/A";
        }
        return returnValue;
      }
      return formatValueByType(
        propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.multiFamilyMixedUse?.valuePerUnit,
        "currency"
      );
    },
    popOverData: (data: any, idx?: number, isAccumulated?: boolean, property?: any) => {
      const { ruleResult, loanStage } = data;
      const { propertiesResults } = ruleResult;
      if (!propertiesResults) return "";
      const propertyIds = Object.keys(propertiesResults);
      if (propertyIds.length <= 1) {
        return [];
      }
      let valueToReturn = [];
      if (isAccumulated) {
        valueToReturn = propertyIds.map((propertyId: any) => {
          if (loanStage === LoanStage.fes) {
            const { minimumArvPerUnit, minimumAsIsPerUnit } =
              propertiesResults[propertyId].guidelineOutput;
            let returnValue = "";
            if (!isEmptyValue(minimumArvPerUnit)) {
              returnValue = `ARV: ${formatValueByType(
                minimumArvPerUnit,
                "currency"
              )}`;
            } else {
              returnValue = "ARV: N/A";
            }
            if (!isEmptyValue(minimumAsIsPerUnit)) {
              returnValue += `; As-Is: ${formatValueByType(
                minimumAsIsPerUnit,
                "currency"
              )}`;
            } else {
              returnValue += "; As-Is: N/A";
            }
            return returnValue;
          }
          return isEmptyValue(
            propertiesResults?.[propertyId]?.multiFamilyMixedUse?.valuePerUnit
          )
            ? "N/A"
            : formatValueByType(
              propertiesResults?.[propertyId]?.multiFamilyMixedUse
                ?.valuePerUnit,
              "currency"
            );
        });
      } else {
        if (loanStage === LoanStage.fes) {
          const { minimumArvPerUnit, minimumAsIsPerUnit } =
            propertiesResults[(idx ?? 0) + 1].guidelineOutput;
          let returnValue = "";
          if (!isEmptyValue(minimumArvPerUnit)) {
            returnValue = `ARV: ${formatValueByType(
              minimumArvPerUnit,
              "currency"
            )}`;
          } else {
            returnValue = "ARV: N/A";
          }
          if (!isEmptyValue(minimumAsIsPerUnit)) {
            returnValue += `; As-Is: ${formatValueByType(
              minimumAsIsPerUnit,
              "currency"
            )}`;
          } else {
            returnValue += "; As-Is: N/A";
          }
          valueToReturn = [returnValue];
        }
        valueToReturn = [
          isEmptyValue(
            propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.multiFamilyMixedUse
              ?.valuePerUnit
          )
            ? "N/A"
            : formatValueByType(
              propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.multiFamilyMixedUse
                ?.valuePerUnit,
              "currency"
            )
        ];
      }
      return valueToReturn;
    }
  },
  {
    fieldId: "purchasePrice",
    fieldLabel: "Purchase Price",
    fieldType: "currency",
    getValue: (data: any) => getAccumlatedPropertyValue(data, "purchasePrice")
  },
  {
    fieldId: "costBasis", // TO-DO: change: after multi family its fes calculation
    fieldLabel: "Cost Basis",
    fieldType: "currency",
    getValue: (data: any) => getAccumlatedPropertyValue(data, "costBasis")
  },
  
  {
    fieldId: "assignmentFees",
    fieldLabel: "Assignment Fee",
    fieldType: "currency",
    showInStage: [LoanStage.fes],
    getValue: (data: any) => getAccumlatedPropertyValue(data, "assignmentFees")
  },
    {
    fieldId: "appraisalStatus",
    fieldLabel: "Appraisal Review Status",
    fieldType: "string",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { propertyDetails, ruleResult } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty > 1 && isAccumulated) {
          const firstKey = Object.keys(ruleResult?.loanResult)[0];
          return ruleResult?.loanResult?.[firstKey]?.loanFeatures
            ?.appraisalStatus || "";
        }
        return (
          ruleResult?.propertiesResults?.[property?.loanPropertyId]?.loanFeatures
            ?.appraisalStatus || ""
        );
      }
    }
  }
];
export const bridgeLoanZillowDataFields: KeyMetricFieldType[] = [
  {
    fieldId: "medianValue",
    fieldLabel: "Median Value",
    fieldType: "currency",
    getValue: (data: any, idx: number) => {
      const { ruleResult } = data;
      const { propertiesResults } = ruleResult;
      if (!propertiesResults) return "";
      const firstProperty = Object.keys(propertiesResults)[idx ?? 0];
      return (
        propertiesResults?.[firstProperty]?.loanFeatures
          ?.medianHomePriceValue ||
        propertiesResults?.[firstProperty]?.loanFeatures?.medianValue
      );
    },
    width: "100px"
  },
  {
    fieldId: "HPA",
    fieldLabel: "HPA",
    fieldType: "percentage",
    getValue: (data: any, idx: number) => {
      const { ruleResult } = data;
      const { propertiesResults } = ruleResult;
      if (!propertiesResults) return "";
      const firstProperty = Object.keys(propertiesResults)[idx ?? 0];
      return (
        propertiesResults?.[firstProperty]?.loanFeatures?.medianHomeHpa ||
        propertiesResults?.[firstProperty]?.loanFeatures?.ltmHpa
      );
    },
    width: "100px"
  },
  {
    fieldId: "DOM",
    fieldLabel: "DOM",
    fieldType: "number",
    getValue: (data: any, idx: number) => {
      const { ruleResult, loanStage } = data;
      const { propertiesResults } = ruleResult;
      if (!propertiesResults) return "";
      const firstProperty = Object.keys(propertiesResults)[idx ?? 0];
      if (loanStage === LoanStage.fes) {
        return propertiesResults?.[firstProperty]?.loanFeatures?.daysOnMarket;
      }
      return propertiesResults?.[firstProperty]?.loanFeatures?.daysOnMarket;
    },
    width: "100px"
  },
];
export const bridgeLoanHouseCanaryInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "hcValue95",
    fieldLabel: "House Canary Value 95",
    fieldType: "currency",
    getValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data;
      const value = ruleResult?.propertiesResults?.[property?.loanPropertyId]?.loanFeatures?.hcValue95;
      return value ? sanitizeCurrency(value) : "";
    },
    customValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data ?? {};
      let value = ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.hcValue95;
      let fontColor = "";
      if (!value) {
        value = "Value Missing";
        fontColor = "#CE1E41";
      } else if (!isNaN(+value)) {
        value = sanitizeCurrency(value);
      }
      return { value, color: fontColor };
    },
    width: "120px"
  },
  {
    fieldId: "hcValueMedian",
    fieldLabel: "House Canary Value Median",
    fieldType: "currency",
    getValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data;
      const value =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ??(idx ?? 0) + 1]?.loanFeatures?.hcValueMedian;
      return value ? sanitizeCurrency(value) : "";
    },
    customValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data ?? {};
      let value =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.hcValueMedian;
      let fontColor = "";
      if (!value) {
        value = "Value Missing";
        fontColor = "#CE1E41";
      } else if (!isNaN(+value)) {
        value = sanitizeCurrency(value);
      }
      return { value, color: fontColor };
    },
    width: "120px"
  },
  {
    fieldId: "varianceHcValue95",
    fieldLabel: "Value 95 to ARV Variance ",
    fieldType: "percentage",
    getValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data;
      const value =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.varianceHcValue95;
      return value ? sanitizePercentage(+value * 100) : "N/A";
    },
    customValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data ?? {};
      let value =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.varianceHcValue95;
      const hcValueMedian =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.hcValueMedian;
      const hcValue95 =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.hcValue95;
      let fontColor = "#1F5BC8";
      if (hcValue95 || hcValueMedian) {
        if (!value) {
          value = "N/A";
          // fontColor = '#CE1E41'
        } else if (!isNaN(+value)) {
          value *= 100;
          fontColor = +value < -10 ? "#CE1E41" : fontColor;
          value = value ? sanitizePercentage(value, false, 2) : "N/A";
        }
        return { value, color: fontColor };
      }
      return "N/A";
    },
    width: "120px"
  },
  {
    fieldId: "varianceHcValueMedian",
    fieldLabel: "Value Median to AIV Variance",
    fieldType: "percentage",
    getValue: (data: any, idx: number) => {
      const { ruleResult } = data;
      const value =
        ruleResult?.loanResult?.[(idx ?? 0) + 1]?.loanFeatures?.varianceHcValueMedian;
      return value ? sanitizePercentage(+value * 100) : "N/A";
    },
    customValue: (data: any, idx: number, isAccumulated: boolean, property?: any) => {
      const { ruleResult } = data ?? {};
      let value =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.varianceHcValueMedian;
      const hcValueMedian =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.hcValueMedian;
      const hcValue95 =
        ruleResult?.propertiesResults?.[property?.loanPropertyId ?? (idx ?? 0) + 1]?.loanFeatures?.hcValue95;
      let fontColor = "#1F5BC8";
      if (hcValue95 || hcValueMedian) {
        if (!value) {
          value = "N/A";
          // fontColor = '#CE1E41'
        } else if (!isNaN(+value)) {
          value *= 100;
          fontColor = +value < -10 ? "#CE1E41" : fontColor;
          value = !isFalsyButNotZero(value) ? sanitizePercentage(value, false, 2) : "N/A";
        }
        return { value, color: fontColor };
      }
      return "N/A";
    },
    width: "120px"
  }
];
export const bridgeLoanRequestedRatiosFields: KeyMetricFieldType[] = [
  {
    fieldId: "LTVAs-Is",
    fieldLabel: "LTV As-Is",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data ?? {};
      const { loanResult } = ruleResult ?? {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanFeatures?.originalAsIsLtv;
    }
  },
  {
    fieldId: "LTVARV",
    fieldLabel: "LTV ARV",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult, loanDetails } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      const isGroundUp: boolean = [loanDetails?.loaninfo?.toorakProduct, loanDetails?.loanSizerType].includes("Ground-Up");
      if (isGroundUp) {
        return loanResult?.[loanId]?.loanFeatures?.arvLtvAsCompleted;
      }
      return (
        loanResult?.[loanId]?.loanFeatures?.originalAsIsRepairedLtv ||
        loanResult?.[loanId]?.loanFeatures?.originalAsRepairedLtv
      );
    }
  },
  {
    fieldId: "LTC",
    fieldLabel: "LTC",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanFeatures.originalLtc;
    }
  },
  {
    fieldId: "LTTC",
    fieldLabel: "LTTC",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanFeatures?.originalLttc;
    }
  },
  {
    fieldId: "blendedLtcAsCompleted",
    fieldLabel: `Blended LTC ("As Completed")`,
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanFeatures?.blendedLtcAsCompleted;
    }
  },
  {
    fieldId: "blendedLTCWithFundedIRAsCompleted",
    fieldLabel: `Blended LTC with Funded IR ("As Completed")`,
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanFeatures
        ?.blendedLTCWithFundedIRAsCompleted;
    }
  }
];

export const bridgeLoanFinancedRatiosFields: KeyMetricFieldType[] = [
  {
    fieldId: "LTVAs-Is",
    fieldLabel: "LTV As-Is",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanFeatures.financedAsIsLtv;
    }
  },
  {
    fieldId: "LTVARV",
    fieldLabel: "LTV ARV",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult?.[loanId]?.loanFeatures?.financedAsRepairedLtv;
    }
  },
  {
    fieldId: "LTC",
    fieldLabel: "LTC",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanFeatures.financedLtc;
    }
  }
];

export const bridgeLoanMaxGuidelineLeverageFields: KeyMetricFieldType[] = [
  {
    fieldId: "LTVAs-Is",
    fieldLabel: "LTV As-Is",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanResult?.[loanId]?.maxLeverage?.maxLTVAsIs ||
        loanResult?.[loanId]?.maxLeverageTest?.maxLTVAsIs
      );
    }
  },
  {
    fieldId: "LTVARV",
    fieldLabel: "LTV ARV",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanDetails } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      const isGroundUp: boolean = [loanDetails?.loaninfo?.toorakProduct, loanDetails?.loanSizerType].includes("Ground-Up");
      if (isGroundUp) {
        return (
          loanResult?.[loanId]?.maxLeverage?.arvLTVAsCompleted ||
          loanResult?.[loanId]?.maxLeverageTest?.arvLTVAsCompleted
        );
      }
      return (
        loanResult?.[loanId]?.maxLeverage?.maxLTVARV ||
        loanResult?.[loanId]?.maxLeverageTest?.maxLTVARV
      );
    }
  },
  {
    fieldId: "LTC",
    fieldLabel: "LTC",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanDetails } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      const isGroundUp: boolean = [loanDetails?.loaninfo?.toorakProduct, loanDetails?.loanSizerType].includes("Ground-Up");
      if (isGroundUp) {
        return (
          loanResult?.[loanId]?.maxLeverage?.maxLTCAsIs ||
          loanResult?.[loanId]?.maxLeverageTest?.maxLTCAsIs
        );
      }
      return (
        loanResult?.[loanId]?.maxLeverage?.maxLTCOne ||
        loanResult?.[loanId]?.maxLeverageTest?.maxLTCOne
      );
    }
  },
  {
    fieldId: "LTTC",
    fieldLabel: "LTTC",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanResult?.[loanId]?.maxLeverage?.maxLttc ||
        loanResult?.[loanId]?.maxLeverageTest?.maxLttc
      );
    }
  },
  {
    fieldId: "maxBlendedLTCAsCompleted",
    fieldLabel: `Blended LTC ("As Completed")`,
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanResult?.[loanId]?.maxLeverageTest?.maxBlendedLTCAsCompleted ||
        loanResult?.[loanId]?.maxLeverage?.maxBlendedLTCAsCompleted
      );
    }
  },
  {
    fieldId: "maxBlendedLTCWithFundedIRAsCompleted",
    fieldLabel: `Blended LTC with Funded IR ("As Completed")`,
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return (
        loanResult?.[loanId]?.maxLeverageTest
          ?.maxBlendedLTCWithFundedIRAsCompleted ||
        loanResult?.[loanId]?.maxLeverage?.maxBlendedLTCWithFundedIRAsCompleted
      );
    }
  }
];
export const bridgeLoanAdjustedRatioFields: KeyMetricFieldType[] = [
  {
    fieldId: "LTVAs-Is",
    fieldLabel: "LTV As-Is",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].finalLeverage.afterAdjustmentMaxLTVAsIs;
      }
      return loanResult[loanId].toorakProceedsTest?.afterAdjustmentMaxLTVAsIs;
    }
  },
  {
    fieldId: "LTVARV",
    fieldLabel: "LTV ARV",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].finalLeverage.afterAdjustmentMaxARV;
      }
      return loanResult[loanId].toorakProceedsTest.afterAdjustmentMaxARV;
    }
  },
  {
    fieldId: "LTC",
    fieldLabel: "LTC",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].finalLeverage.afterAdjustmentMaxLTC;
      }
      return loanResult[loanId].toorakProceedsTest.afterAdjustmentMaxLTC;
    }
  },
  {
    fieldId: "LTTC",
    fieldLabel: "LTTC",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].finalLeverage.afterAdjustmentMaxLTTC;
      }
      return loanResult[loanId].toorakProceedsTest.afterAdjustmentMaxLTTC;
    }
  },
  {
    fieldId: "afterAdjustmentMaxBlendedLTC",
    fieldLabel: `Blended LTC ("As Completed")`,
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].finalLeverage.afterAdjustmentMaxBlendedLTC;
      }
      return loanResult[loanId].toorakProceedsTest.afterAdjustmentMaxBlendedLTC;
    }
  },
  {
    fieldId: "afterAdjustmentMaxBlendedLTCWithIR",
    fieldLabel: `Blended LTC with Funded IR ("As Completed")`,
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].finalLeverage
          .afterAdjustmentMaxBlendedLTCWithIR;
      }
      return loanResult[loanId].toorakProceedsTest
        .afterAdjustmentMaxBlendedLTCWithIR;
    }
  }
];
export const bridgeLoanRCNGeneralFields: KeyMetricFieldType[] = [
  {
    fieldId: "fitsOverlaysTest",
    fieldLabel: "Fits Overlays",
    fieldType: "string",
    showInStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
    isHidden: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult)[0];
      if (data?.loanStage === LoanStage.fes) {
        return [null, "null"].includes(
          loanResult?.[loanId]?.loanCharacterisations?.fitsOverlaysTest
            ? "PASS"
            : "FAIL"
        );
      }
      return [null, "null"].includes(
        loanResult?.[loanId]?.loanCharacterisations?.fitsOverlaysTest
      );
    },
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult)[0];
      return [true, "PASS", "True"].includes(
        loanResult?.[loanId]?.loanCharacterisations?.fitsOverlaysTest
      )
        ? "Yes"
        : "No";
    }
  }
];
export const bridgeLeverageAndRatiosTableHeaders = [
  { field: "label", headerName: "Leverages & Ratios" },
  { field: "LTVAs-Is", headerName: "LTV As-Is", align: "left" },
  { field: "LTVARV", headerName: "LTV ARV", align: "left" },
  { field: "LTC", headerName: "LTC", align: "left" },
  { field: "LTTC", headerName: "LTTC", align: "left" },
  { field: "blendedLtcAsCompleted", headerName: "Blended LTC (As Completed)", align: "left" },
  { field: "blendedLTCWithFundedIRAsCompleted", headerName: "Blended LTC with Funded IR (As Completed)", align: "right" },
];

export const dscrLeverageAndRatiosTableHeaders = [
  { field: "label", headerName: "Leverages & Ratios" },
  { field: "As Is-LTV", headerName: '"As Is" LTV', align: "left" },
  // { field: "LTC", headerName: "LTC", align: "left" },
];

export const bridgeAdditionalOverlayGuidelines = (data: any) => {
  const { ruleResult, orgSpecificTestsLabels } = data;
  const { loanResult } = ruleResult;
  const loanId = Object.keys(loanResult)[0];
  const { orgSpecificTests } = loanResult?.[loanId] ?? {}
  const keys = Object.keys(orgSpecificTests || {});
  return keys.map((key: string) => ({
    fieldId: key,
    fieldLabel: eval(orgSpecificTestsLabels?.[`testNames.${key}`]) || key,
    fieldType: "string",
    showInStage: [LoanStage.pre, LoanStage.post, LoanStage.fes],
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult)[0];
      return [true, "PASS", "True"].includes(
        loanResult?.[loanId]?.orgSpecificTests[key]
      )
        ? "PASS"
        : "FAIL";
    }
  }))
};

export const bridgeLoanKeyMetricSection = (data?: any) => ([
  {
    sectionId: "general",
    sectionLabel: "General",
    fields: bridgeLoanGeneralFields,
  },
  {
    sectionId: "borrowerInfo",
    sectionLabel: "Borrower Information",
    fields: bridgeLoanBorrowerInfoFields,
  },
  {
    sectionId: "rcnOverlays",
    sectionLabel: "Overlays",
    fields: [...bridgeLoanRCNGeneralFields, ...bridgeAdditionalOverlayGuidelines(data)],
    isHidden: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult)[0];

      const fitOverlaysTestNullCheck = [null, "null"].includes(
        loanResult?.[loanId]?.loanCharacterisations?.fitsOverlaysTest
      );

      return fitOverlaysTestNullCheck;
    },
  },
  {
    sectionId: "loanInfo",
    sectionLabel: "Loan Information",
    fields: bridgeLoanLoanInfoFields,
  },
  {
    sectionId: "propertyInfo",
    sectionLabel: "Property Information",
    fields: bridgeLoanPropertyInfoFields,
  },
  {
    sectionId: "zillowData",
    sectionLabel: "Zillow Data",
    fields: bridgeLoanZillowDataFields,
  },
  {
    sectionId: "houseCanaryInfo",
    sectionLabel: "House Canary Information",
    fields: bridgeLoanHouseCanaryInfoFields,
  },
  {
    sectionId: "leveragesAndRatios",
    sectionLabel: "Leverages and Ratios",
    fields: bridgeLeverageAndRatiosTableHeaders,
    type: "table"
  },
    // {
    //   sectionId: "financedRatios",
    //   sectionLabel: "Financed Ratios",
    //   fields: bridgeLoanFinancedRatiosFields,
    //   minWidth: "100",
    //   sortIdx: 8
    // },
    // {
    //   sectionId: "maxGuidelineLeverage",
    //   sectionLabel: "Maximum Guideline Leverage",
    //   fields: bridgeLoanMaxGuidelineLeverageFields,
    //   minWidth: "100",
    //   sortIdx: 8
    // },
    // {
    //   sectionId: "adjustedRatios",
    //   sectionLabel: "Adjusted Ratios",
    //   fields: bridgeLoanAdjustedRatioFields,
    //   minWidth: "100",
    //   sortIdx: 8
    // }
]);

export const fesBridgeLoanKeyMetricSection = (data: any) => bridgeLoanKeyMetricSection(data)?.filter((elem: any) =>  elem?.sectionId !== "financedRatios");

export const prePostBridgeLoanKeyMetricSection = (data: any) => {
  const fields = bridgeLoanKeyMetricSection(data);
  return isLATUser() ? fields : fields?.filter((elem: any) =>  elem?.sectionId !== "financedRatios")
}

/** --------------------------- BridgeLoan section end --------------------------- */

/** --------------------------- Investor DSCR section start ---------------------------*/
export const investorDscrLoanInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "loanId",
    fieldLabel: "Loan ID",
    fieldType: "string",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanInfo?.primaryLoanId;
    }
  },
  {
    fieldId: "originator",
    fieldLabel: "Originator",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.originatorInfo?.accountName;
    }
  },
  {
    fieldId: "toorakProduct",
    fieldLabel: "Toorak Product",
    fieldType: "string",
    getValue: (data: any) => "30 Yr Rental"
  },
  {
    fieldId: "toorakCharacterization",
    fieldLabel: "Toorak Characterization",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails, loanStage, ruleResult } = data;

      if (loanStage === LoanStage.fes) {
        const { loanResult } = ruleResult;
        if (!loanResult) return "";
        const loanId = Object.keys(loanResult)[0];
        return loanResult?.[loanId]?.loanCharacterization
          .finalLoanCharacterization;
      }
      return loanDetails?.loanInfo?.loanPurpose;
    }
  }
];
export const investorDscrBorrowerInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "creditScore",
    fieldLabel: "Credit Score",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.borrowerInfo?.originalCreditScoreMedian;
    }
  }
];
export const investorDscrLoanEconomicsField: KeyMetricFieldType[] = [
  {
    fieldId: "originalLoanAmount",
    fieldLabel: "Original Loan Amount",
    fieldType: "currency",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.originalLoanAmount;
    }
  },
  {
    fieldId: "interestRate",
    fieldLabel: "Interest Rate",
    fieldType: "percentageWithout100x",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.interestRateAsOfCutOffDate;
    }
  },
  {
    fieldId: "originalTermToMaturity",
    fieldLabel: "Original Term to Maturity(Months)",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        const { loanDetails } = data;
        return loanDetails?.loanInfo?.loanTermMonthly;
      }
      return loanResult[loanId].loanFeatures.originalTermToMaturity;
    }
  },
  {
    fieldId: "ioPeriods",
    fieldLabel: "IO Period(Months)",
    fieldType: "number",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.interestOnlyPeriod;
    }
  },
  {
    fieldId: "prepaymentPenaltyMonths",
    fieldLabel: "Prepayment Penalty(Months)",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult, loanDetails, loanStage } = data;
      if (loanStage === LoanStage.fes) {
        return loanDetails?.loanEconomics?.prepaymentPenaltyMonths;
      }
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanEconomics.prePaymentPenaltyMonths;
    }
  },
  {
    fieldId: "cashOutAmount",
    fieldLabel: "Cash Out Amount",
    fieldType: "currency",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.cashOutAmount;
    }
  },
  {
    fieldId: "DSCR",
    fieldLabel: "DSCR",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanEconomics.dscr;
    }
  },
  {
    fieldId: "grossPotentialRent",
    fieldLabel: "Monthly Property Gross Rent",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId
        ? loanResult[loanId].loanEconomics?.aggregateMonthlyPropertyGrossRent
        : "";
    }
  },
  {
    fieldId: "monthlyInterest",
    fieldLabel: "Monthly Interest",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId ? loanResult[loanId].loanEconomics?.monthlyInterest : "";
    }
  },
  {
    fieldId: "monthlyPrincipalDuringAmortization",
    fieldLabel: "Monthly Principal During Amortization",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId
        ? loanResult[loanId].loanEconomics?.monthlyPrincipalDuringAmortization
        : "";
    }
  },
  {
    fieldId: "aggregateAnnualPropertyTaxes",
    fieldLabel: "Monthly Taxes",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId
        ? loanResult[loanId].loanEconomics?.aggregateAnnualPropertyTaxes
        : "";
    }
  },
  {
    fieldId: "aggregateMonthlyAnnualHazardInsurance",
    fieldLabel: "Monthly Insurance",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId
        ? loanResult[loanId].loanEconomics
          ?.aggregateMonthlyAnnualHazardInsurance
        : "";
    }
  },
  {
    fieldId: "aggregateMonthlyAnnualFloodInsurance",
    fieldLabel: "Monthly Flood Insurance",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId
        ? loanResult[loanId].loanEconomics?.aggregateMonthlyAnnualFloodInsurance
        : "";
    }
  },
  {
    fieldId: "aggregateMonthlyAnnualHOA",
    fieldLabel: "Monthly HOA",
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult || {};
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      return loanId
        ? loanResult[loanId].loanEconomics?.aggregateMonthlyAnnualHOA
        : "";
    }
  },
  {
    fieldId: "rateType",
    fieldLabel: "Rate Type",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanEconomics?.rateType;
    }
  },
  {
    fieldId: "requiredReserves",
    fieldLabel: "Required reserves",
    showInStage: [LoanStage.fes],
    fieldType: "currency",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId]?.loanEconomics?.minimumBorrowerLiquidity;
    }
  },
  {
    fieldId: "qualifyingPaymentInReservesMonthly",
    fieldLabel: "PITIA",
    showInStage: [LoanStage.pre, LoanStage.post],
    fieldType: "decimal",
    isHidden: (data: any) => {
      const { loanDetails } = data;
      const { loanEconomics } = loanDetails;
      const { interestOnlyPeriod } = loanEconomics;
      return interestOnlyPeriod !== 0;
    },
    getValue: (data: any) => {
    const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      if (!loanResult) return "";
      const { monthlyQualifyingPayment } = loanResult[loanId]?.loanEconomics || {};
      return (+monthlyQualifyingPayment)?.toFixed(2);
    }
  },
  {
    fieldId: "iti",
    fieldLabel: "ITIA",
    showInStage: [LoanStage.pre, LoanStage.post],
    fieldType: "decimal",
    isHidden: (data: any) => {
      const { loanDetails } = data;
      const { loanEconomics } = loanDetails;
      const { interestOnlyPeriod } = loanEconomics;
      return interestOnlyPeriod === 0;
    },
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      const loanId = Object.keys(loanResult).length
        ? Object.keys(loanResult)[0]
        : "";
      if (!loanResult) return "";
      const { monthlyQualifyingPayment } = loanResult[loanId]?.loanEconomics || {};
      return (+monthlyQualifyingPayment)?.toFixed(2);
    }
  }
];
export const investorDscrPropertyInfoFields: KeyMetricFieldType[] = [
  {
    fieldId: "address",
    fieldLabel: "Address",
    fieldType: "string",
    redirectionType: "googleMap",
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty > 1 && isAccumulated) {
          return `Multiple Properties-${numberOfProperty}`;
        }
        const { propertyLocation } = propertyDetails[idx ?? 0];
        const { addressLine1, city, state, postalCode } = propertyLocation;
        return `${addressLine1}, ${city}, ${state}, ${postalCode}`;
      }
    },
    popOverData: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty <= 1) {
          return [];
        }
        if (isAccumulated && propertyDetails?.length > 1) {
          const propertyAddressArray = propertyDetails.map((ele: any) => {
            const { propertyLocation } = ele;
            const { addressLine1, city, state, postalCode } = propertyLocation;
            return `${addressLine1}, ${city}, ${state}, ${postalCode}`;
          });
          return propertyAddressArray;
        }
        const { propertyLocation } = propertyDetails[idx ?? 0];
        const { addressLine1, city, state, postalCode } = propertyLocation;
        return [`${addressLine1}, ${city}, ${state}, ${postalCode}`];
      }
      return [];
    }
  },
  {
    fieldId: "propertyType",
    fieldLabel: "Property Type",
    fieldType: "string",
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty > 1 && isAccumulated) {
          return `Multiple Properties-${numberOfProperty}`;
        }
        const { propertyinfo } = propertyDetails[idx ?? 0];
        const propertyType =
          propertyinfo.propertyType === "SFR"
            ? "Single Family Detached"
            : propertyinfo.propertyType;
        return propertyType;
      }
    },
    popOverData: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      if (propertyDetails) {
        const numberOfProperty = propertyDetails.length;
        if (numberOfProperty <= 1) {
          return [];
        }
        if (isAccumulated && propertyDetails?.length > 1) {
          return propertyDetails.map((ele: any) => {
            const { propertyinfo } = ele;
            return propertyinfo.propertyType === "SFR"
              ? "Single Family Detached"
              : propertyinfo.propertyType;
          });
        }
        const { propertyinfo } = propertyDetails[idx ?? 0];
        const propertyType =
          propertyinfo.propertyType === "SFR"
            ? "Single Family Detached"
            : propertyinfo.propertyType;
        return [propertyType];
      }
      return [];
    }
  },
  {
    fieldId: "units",
    fieldLabel: "Units",
    fieldType: "number",
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      let numberOfUnits: any = null;
      if (propertyDetails?.length > 1 && isAccumulated) {
        propertyDetails.forEach((element: any) => {
          const { propertyinfo } = element;
          if (!isEmptyValue(propertyinfo.propertyUnits)) {
            if (numberOfUnits) {
              numberOfUnits += Number(propertyinfo.propertyUnits);
            } else {
              numberOfUnits = Number(propertyinfo.propertyUnits);
            }
          }
        });
      } else {
        const { propertyinfo } = propertyDetails[idx ?? 0] || {};
        numberOfUnits = propertyinfo?.propertyUnits || null;
      }
      return numberOfUnits;
    }
  },
  {
    fieldId: "asIs-original",
    fieldLabel: 'Original "As Is" Appraisal Value',
    fieldType: "currency",
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      let valueToReturn: any = null;

      if (!propertyDetails) return "";
      if (propertyDetails?.length > 1 && isAccumulated) {
        return getAccumlatedPropertyValue(data, "originalAsIsAppraisalValue");
      }
      const { propertyEconomics } = propertyDetails[idx ?? 0];
      if (!isEmptyValue(propertyEconomics?.["originalAsIsAppraisalValue"])) {
        const cleanedValue = currencyToNumber(propertyEconomics?.["originalAsIsAppraisalValue"]);
        if (valueToReturn) {
          valueToReturn += Number(cleanedValue);
        } else {
          valueToReturn = Number(cleanedValue);
        }
      }
      return `${valueToReturn ?? ""}`;
    }
  },
  {
    fieldId: "percentOccupied",
    fieldLabel: "Occupancy %",
    fieldType: "percentage",
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { ruleResult, propertyDetails } = data;
      const { propertiesResults } = ruleResult;
      if (!propertiesResults) return "";
      let valueToReturn: any;
      const propertyKeys = Object.keys(propertiesResults);
      if (propertyKeys?.length > 1 && isAccumulated) {
        propertyKeys.forEach((key) => {
          const percentOccupied = Number(
            propertiesResults?.[key]?.loanEconomics?.percentOccupied
          );
          if (!isNaN(percentOccupied)) {
            if (!isEmptyValue(valueToReturn)) {
              valueToReturn += percentOccupied;
            } else {
              valueToReturn = percentOccupied;
            }
          }
        });
      }
      const percentOccupied = Number(
        propertiesResults?.["1"]?.loanEconomics?.percentOccupied
      );
      if (!isNaN(percentOccupied)) {
        if (!isEmptyValue(valueToReturn)) {
          valueToReturn += percentOccupied;
        } else {
          valueToReturn = percentOccupied;
        }
      }
      return valueToReturn && isAccumulated
        ? valueToReturn / propertyKeys.length
        : valueToReturn;
    }
  }
];
export const investorDscrPropertyEconomicsFields: KeyMetricFieldType[] = [
  {
    fieldId: "purchasePrice",
    fieldLabel: "Purchase Price",
    fieldType: "currency",
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      let valueToReturn: any = null;
      if (!propertyDetails) return "";
      const element = propertyDetails[idx ?? 0];
      if (data?.propertyDetails?.length > 1 && isAccumulated) {
        return getAccumlatedPropertyValue(data, "purchasePrice");
      }
      const { propertyEconomics } = element;
      if (!isEmptyValue(propertyEconomics?.["purchasePrice"])) {
        const cleanedValue = currencyToNumber(propertyEconomics?.["purchasePrice"]);
        if (valueToReturn) {
          valueToReturn += Number(cleanedValue);
        } else {
          valueToReturn = Number(cleanedValue);
        }
      }
      return `${valueToReturn ?? ""}`;
    }
  },
  {
    fieldId: "costBasis",
    fieldLabel: "Cost Basis",
    fieldType: "currency",
    showInStage: [LoanStage.pre, LoanStage.post],
    getValue: (data: any, idx: number, isAccumulated: boolean) => {
      const { propertyDetails } = data;
      let valueToReturn: any = null;
      if (!propertyDetails) return "";
      const element = propertyDetails[idx ?? 0];
      if (data?.propertyDetails?.length > 1 && isAccumulated) {
        return getAccumlatedPropertyValue(data, "costBasis");
      }
      const { propertyEconomics } = element;
      if (!isEmptyValue(propertyEconomics?.["costBasis"])) {
        const cleanedValue = currencyToNumber(propertyEconomics?.["costBasis"]);
        if (valueToReturn) {
          valueToReturn += Number(cleanedValue);
        } else {
          valueToReturn = Number(cleanedValue);
        }
      }
      return `${valueToReturn ?? ""}`;
    }
  }
];
export const investorDSCRActualRatiosFields: KeyMetricFieldType[] = [
  {
    fieldId: "Original-As Is-LTV",
    fieldLabel: 'Original "As Is" LTV',
    id: "As Is-LTV",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanFeatures.originalAsIsLtv;
    }
  },
  {
    fieldId: "LTC",
    fieldLabel: "Original LTC/LTP",
    id: "LTC",
    fieldType: "percentageTwoDecimal",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanFeatures.originalLtc;
    }
  }
];

export const investorDscrMaxGuidelineLeverageFields: KeyMetricFieldType[] = [
  {
    fieldId: "MaxAs-IsLTV",
    fieldLabel: "Max As-Is LTV",
    id: "As Is-LTV",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].maxLeverage.maxAsIsLTV;
    }
  },
  {
    fieldId: "LTC",
    fieldLabel: "Max LTC",
    id: "LTC",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].maxLeverage.maxLTC;
    }
  }
];
export const investorDscrNetLeverageFields: KeyMetricFieldType[] = [
  {
    fieldId: "afterAdjustment-LTVAs-Is",
    fieldLabel: "Afer Adjustment: Max As-Is LTV",
    id: "As Is-LTV",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].toorakProceeds.toorakAdjustedAsIsLtv;
      }
      return loanResult[loanId].toorakProceeds.afterAdjustmentMaxLTVAsIs;
    }
  },
  {
    fieldId: "afterAdjustment-LTC",
    fieldLabel: "Afer Adjustment: Max LTC",
    id: "LTC",
    fieldType: "percentage",
    getValue: (data: any) => {
      const { ruleResult, loanStage } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      if (loanStage === LoanStage.fes) {
        return loanResult[loanId].toorakProceeds.toorakAdjustedAsIsLtc;
      }
      return loanResult[loanId].toorakProceeds.afterAdjustmentMaxLTC;
    }
  }
];

const getRateLockExpiryDate = (
  initialDate: string,
  finalExtensionDays: number
) => {
  if (isEmptyValue(initialDate)) return "N/A";
  const expiry = momentz
    .utc(initialDate)
    .subtract(parseInt(`${finalExtensionDays}`, 10), "days");
  const expiryDate = `${expiry.format("MMM DD, YYYY")}`;
  return `${expiryDate}`;
};

export const investorDSCRRateLockFields: KeyMetricFieldType[] = [
  {
    fieldId: "rate-sheet-date",
    fieldLabel: "Rate Sheet Date",
    fieldType: "date",
    getValue: (data: any) => {
      const { loanDetails } = data;
      const rateSheetDate =
        loanDetails?.loanDetailId?.loanConfigId?.rateLockedRateSheet
          ?.replace(/\D/g, "")
          .trim();
      return loanDetails?.loanDetailId?.loanConfigId?.rateLockedRateSheet
        ? momentz(rateSheetDate, "DD-MM-YY").format("MMM DD, YYYY")
        : "-";
    }
  },
  {
    fieldId: "initial-lock-date",
    fieldLabel: "Initial Lock Date",
    fieldType: "date",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanDetailId?.loanConfigId?.rateLockedDate
        ? momentz(
          loanDetails?.loanDetailId?.loanConfigId?.rateLockedDate
        ).format("MMM DD, YYYY")
        : "-";
    }
  },
  {
    fieldId: "initial-lock-term",
    fieldLabel: "Initial Lock Term",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.loanDetailId?.loanConfigId?.rateLockPeriod
        ? `${loanDetails?.loanDetailId?.loanConfigId?.rateLockPeriod} Days`
        : "-";
    }
  },
  {
    fieldId: "initial-lock-term-cost",
    fieldLabel: "Initial Lock Term Cost",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanPricing?.rateLockAdjustment ?? "-";
    }
  },
  {
    fieldId: "initial-rate-lock-expiry-date",
    fieldLabel: "Initial Rate Lock Expiry Date",
    fieldType: "date",
    getValue: (data: any) => {
      const { loanDetails } = data;
      let finalCount = 0;
      if (Array.isArray(loanDetails.loanDetailId?.rateLockExtensions)) {
        loanDetails.loanDetailId?.rateLockExtensions?.map((extension: any) => {
          finalCount += extension?.extensionDays;
        });
        return loanDetails?.rateLockExpiryDate
          ? getRateLockExpiryDate(loanDetails?.rateLockExpiryDate, finalCount)
          : "-";
      }
    }
  },
  {
    fieldId: "is-extension",
    fieldLabel: "Is Extension",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return Array.isArray(loanDetails.loanDetailId?.rateLockExtensions) &&
        loanDetails.loanDetailId?.rateLockExtensions?.length
        ? "Yes"
        : "No";
    }
  },
  {
    fieldId: "extension-days",
    fieldLabel: "Extension Term",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      let finalCount = 0;
      if (Array.isArray(loanDetails.loanDetailId?.rateLockExtensions)) {
        loanDetails.loanDetailId?.rateLockExtensions?.map((extension: any) => {
          finalCount += extension?.extensionDays;
        });
      }
      return finalCount ? `${finalCount} Days` : "-";
    }
  },
  {
    fieldId: "extension-cost",
    fieldLabel: "Extension Cost",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanPricing?.rateLockExtensionAdjustment ?? "-";
    }
  },
  {
    fieldId: "final-lock-term",
    fieldLabel: "Final Lock Term",
    fieldType: "string",
    getValue: (data: any) => {
      const { loanDetails } = data;
      let finalCount =
        loanDetails?.loanDetailId?.loanConfigId?.rateLockPeriod ?? 0;
      if (Array.isArray(loanDetails.loanDetailId?.rateLockExtensions)) {
        loanDetails.loanDetailId?.rateLockExtensions?.map((extension: any) => {
          finalCount += extension?.extensionDays;
        });
      }
      return finalCount ? `${finalCount} Days` : "-";
    }
  },
  {
    fieldId: "final-cost",
    fieldLabel: "Final Lock Term Cost",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      const result =
        parseFloat(
          loanResult[loanId]?.loanPricing?.rateLockExtensionAdjustment ?? "0"
        ) +
        parseFloat(loanResult[loanId]?.loanPricing?.rateLockAdjustment ?? "0");
      return result ?? "-";
    }
  },
  {
    fieldId: "current-lock-expiry-date",
    fieldLabel: "Current Lock Expiry Date",
    fieldType: "date",
    getValue: (data: any) => {
      const { loanDetails } = data;
      return loanDetails?.rateLockExpiryDate
        ? momentz(loanDetails?.rateLockExpiryDate).format("MMM DD, YYYY")
        : "-";
    }
  },
  {
    fieldId: "final-price",
    fieldLabel: "Final Price",
    fieldType: "number",
    getValue: (data: any) => {
      const { ruleResult } = data;
      const { loanResult } = ruleResult;
      if (!loanResult) return "";
      const loanId = Object.keys(loanResult)[0];
      return loanResult[loanId].loanPricing?.finalToorakPrice ?? "N/A";
    }
  }
];

export const investorDSCRLoanKeyMetricSection: Array<KeyMetricSectionType> = [
  {
    sectionId: "loanInformation",
    sectionLabel: "Loan Information",
    fields: investorDscrLoanInfoFields,
  },
  {
    sectionId: "borrowerInfo",
    sectionLabel: "Borrower Information",
    fields: investorDscrBorrowerInfoFields
  },
  {
    sectionId: "loanEconomics",
    sectionLabel: "Loan Economics",
    fields: investorDscrLoanEconomicsField
  },
  {
    sectionId: "propertyInfo",
    sectionLabel: "Property Information",
    fields: investorDscrPropertyInfoFields,
  },
  {
    sectionId: "zillowData",
    sectionLabel: "Zillow Data",
    fields: bridgeLoanZillowDataFields,
  },
  {
    sectionId: "houseCanaryInfo",
    sectionLabel: "House Canary Information",
    fields: bridgeLoanHouseCanaryInfoFields,
  },
  {
    sectionId: "propertyEconomics",
    sectionLabel: "Property Economics",
    fields: investorDscrPropertyEconomicsFields
  },
  {
    sectionId: "leveragesAndRatios",
    sectionLabel: "Leverages and Ratios",
    fields: dscrLeverageAndRatiosTableHeaders,
    type: "table"
  },
  // {
  //   sectionId: "actualRatios",
  //   sectionLabel: "Actual Ratios",
  //   fields: investorDSCRActualRatiosFields
  // },
  // {
  //   sectionId: "maxGuidelineLeverage",
  //   sectionLabel: "Max Leverages",
  //   fields: investorDscrMaxGuidelineLeverageFields
  // },
  // {
  //   sectionId: "netLeverage",
  //   sectionLabel: "Net Leverages",
  //   fields: investorDscrNetLeverageFields
  // },
  {
    sectionId: "rateLockFields",
    sectionLabel: "Lock Info",
    fields: investorDSCRRateLockFields
  }
];
/** --------------------------- Investor DSCR section end ---------------------------*/
